import { ApiError, httpClient } from './httpClient';
import type {
  AdminBanner,
  CreateBannerPayload,
  PublicBanner,
  UpdateBannerPayload,
} from '../types/banner';

const ensureSuccess = <T extends { success?: boolean; message?: string }>(payload: T) => {
  if (payload?.success === false) {
    throw new ApiError(payload.message || 'Banner API error', 400, payload);
  }
  return payload;
};

type Envelope<T> = {
  success?: boolean;
  message?: string;
  data?: T;
};

export type AdminBannersList = {
  banners: AdminBanner[];
  total: number;
  page: number;
  limit: number;
};

const unwrapBanner = (payload: Envelope<{ banner?: PublicBanner | null } | PublicBanner | null>): PublicBanner | null => {
  const data = payload?.data;
  if (data == null) return null;
  if (typeof data === 'object' && 'banner' in data) {
    return data.banner ?? null;
  }
  if (typeof data === 'object' && 'id' in data) {
    return data as PublicBanner;
  }
  return null;
};

const unwrapAdminBanner = (
  payload: Envelope<{ banner?: AdminBanner } | AdminBanner>,
): AdminBanner => {
  const data = payload?.data;
  if (data && typeof data === 'object' && 'banner' in data && data.banner) {
    return data.banner;
  }
  if (data && typeof data === 'object' && 'id' in data) {
    return data as AdminBanner;
  }
  throw new ApiError(payload?.message || 'Некорректный ответ баннера', 400, payload);
};

const unwrapAdminList = (payload: Envelope<any>, page: number, limit: number): AdminBannersList => {
  const data = payload?.data;
  if (Array.isArray(data)) {
    return { banners: data, total: data.length, page, limit };
  }
  const banners: AdminBanner[] =
    data?.banners ?? data?.items ?? data?.rows ?? [];
  const total = typeof data?.total === 'number' ? data.total : banners.length;
  return {
    banners,
    total,
    page: typeof data?.page === 'number' ? data.page : page,
    limit: typeof data?.limit === 'number' ? data.limit : limit,
  };
};

/** Публичный снимок: один живой баннер или null. Работает без авторизации. */
export const getPublicBanner = async (): Promise<PublicBanner | null> => {
  const response = await httpClient.request<Envelope<{ banner?: PublicBanner | null } | PublicBanner | null>>(
    '/banners',
  );
  return unwrapBanner(ensureSuccess(response));
};

export const getAdminBanners = async (params?: {
  page?: number;
  limit?: number;
}): Promise<AdminBannersList> => {
  const page = params?.page ?? 1;
  const limit = Math.min(params?.limit ?? 20, 100);
  const query = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  const response = await httpClient.request<Envelope<any>>(`/admin/banners?${query}`);
  return unwrapAdminList(ensureSuccess(response), page, limit);
};

export const getAdminBanner = async (id: string): Promise<AdminBanner> => {
  const response = await httpClient.request<Envelope<{ banner?: AdminBanner } | AdminBanner>>(
    `/admin/banners/${id}`,
  );
  return unwrapAdminBanner(ensureSuccess(response));
};

export const createAdminBanner = async (payload: CreateBannerPayload): Promise<AdminBanner> => {
  const response = await httpClient.request<Envelope<{ banner?: AdminBanner } | AdminBanner>>(
    '/admin/banners',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
  return unwrapAdminBanner(ensureSuccess(response));
};

export const updateAdminBanner = async (
  id: string,
  payload: UpdateBannerPayload,
): Promise<AdminBanner> => {
  const response = await httpClient.request<Envelope<{ banner?: AdminBanner } | AdminBanner>>(
    `/admin/banners/${id}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );
  return unwrapAdminBanner(ensureSuccess(response));
};

export const deleteAdminBanner = async (id: string): Promise<void> => {
  const response = await httpClient.request<Envelope<unknown>>(`/admin/banners/${id}`, {
    method: 'DELETE',
  });
  ensureSuccess(response);
};
